import { REMOTE_RECORD_ACTION_INIT, RemoteRecordDataType } from '@client/common/redux/RemoteRecordData';
import { Action, Selector } from '@reduxjs/toolkit';
import type { PutEffect, SelectEffect } from 'redux-saga/effects';
import { put, select } from 'redux-saga/effects';

export type RemoteRecordCleanUpAction<T> = {
  type: string;
  payload: {
    records: Record<string, RemoteRecordDataType<T>>;
    triggeredBy: string;
    previousLength: number;
  };
};
export const REMOTE_RECORD_CLEAN_UP_DEBOUNCE = 60 * 1000;

export enum REMOTE_RECORD_LIMITS {
  xsmall = 10,
  small = 25,
  medium = 50,
  large = 100,
  xlarge = 600,
}

export function createRemoteRecordCleanUpSaga<T>(
  selectSlice: Selector<any, Record<string, RemoteRecordDataType<T>>>,
  cleanUpActionType: Action['type'],
  maxCount: REMOTE_RECORD_LIMITS | number = REMOTE_RECORD_LIMITS.small,
) {
  return function* cleanUpSaga(
    action: Action,
  ): Generator<
    SelectEffect | PutEffect,
    void,
    Record<string, RemoteRecordDataType<T>>
  > {
    const entriesObj: Record<string, RemoteRecordDataType<T>> = yield select(selectSlice);
    const keys = Object.keys(entriesObj);

    if (keys.length <= maxCount) {
      return;
    }

    const unresolvedRecords = keys.filter(
      key => !entriesObj[key].status || entriesObj[key].status === REMOTE_RECORD_ACTION_INIT,
    );
    const resolvedRecords = keys.filter(
      key => !!entriesObj[key].status && entriesObj[key].status !== REMOTE_RECORD_ACTION_INIT,
    );

    const resolvedRecordsToSave = resolvedRecords
      .sort((a, b) => (entriesObj[b].timestamp ?? 0) - (entriesObj[a].timestamp ?? 0))
      .slice(0, maxCount);

    const recordsToSave = [...unresolvedRecords, ...resolvedRecordsToSave];

    if (recordsToSave.length === keys.length) {
      return;
    }

    const records: Record<string, RemoteRecordDataType<T>> = {};
    for (const key of recordsToSave) {
      records[key] = entriesObj[key];
    }

    yield put({
      type: cleanUpActionType,
      payload: {
        newLength: recordsToSave.length,
        previousLength: keys.length,
        triggeredBy: action.type,
        records,
      },
    } as RemoteRecordCleanUpAction<T>);
  };
}
